import React, { useState, useEffect, useMemo } from 'react';
import { apiService, graphBuilderApi } from '../../../services/api';
import {
  STAGE_MODEL_KEYS,
  MODEL_PRESETS,
  CONTEXT_POTENCY_MODE_OPTIONS,
} from '../../../constants/pipelineSettings';

const PROVIDER_TYPE_LABELS = {
  openai: 'OpenAI',
  do_serverless: 'DO Serverless',
  do_droplet: 'DO Droplet',
  do_dedicated: 'DO Dedicated',
};

const ENGINE_MODES = [
  { value: 'compiled_full', label: 'Compiled Full (Default)' },
  { value: 'compiled_full_numpy', label: 'Compiled Full - Fast Engine' },
  { value: 'not_compiled', label: 'Not Compiled (Debug)' },
  { value: 'compiled_sharded', label: 'Compiled Sharded (Legacy)' },
];

const EXECUTION_MODES = [
  { value: 'BUILD_AND_RUN', label: 'Build & Run (Full Pipeline)' },
  { value: 'BUILD_ONLY', label: 'Build Only (No LLM Response)' },
];

function SNNSettingsPanel({ settings, onChange, graphId }) {
  const [vocabWarning, setVocabWarning] = useState('');
  const [compileModeWarning, setCompileModeWarning] = useState('');
  const [snnTuningOpen, setSnnTuningOpen] = useState(false);
  const [stageModelsOpen, setStageModelsOpen] = useState(false);
  const [mlcfOpen, setMlcfOpen] = useState(false);
  const [crsOpen, setCrsOpen] = useState(false);
  const [serOpen, setSerOpen] = useState(false);
  const [llmProviders, setLlmProviders] = useState([]);

  const LLM_MODEL_OPTIONS = useMemo(() => {
    const providerOpts = llmProviders.map((p) => ({
      value: `provider::${p.id}::${p.model_name}`,
      label: `${p.model_name} (${PROVIDER_TYPE_LABELS[p.provider_type] || p.provider_type} - ${p.name})`,
      provider_id: p.id,
      model_name: p.model_name,
      is_default: p.is_default,
    }));
    return providerOpts;
  }, [llmProviders]);

  useEffect(() => {
    apiService.listLLMProviders().then((d) => setLlmProviders(d.providers || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!llmProviders.length) return;
    const defaultProvider = llmProviders.find((p) => p.is_default) || llmProviders[0];
    if (defaultProvider && !settings.llm_model?.startsWith('provider::')) {
      onChange('llm_model', `provider::${defaultProvider.id}::${defaultProvider.model_name}`);
    }
  }, [llmProviders]); // eslint-disable-line react-hooks/exhaustive-deps

  const isCompiledMode = settings.snn_execution_mode === 'compiled_full'
    || settings.snn_execution_mode === 'compiled_full_numpy'
    || settings.snn_execution_mode === 'compiled_sharded';

  const handleSnnModeChange = async (newMode) => {
    if (newMode === 'not_compiled') {
      onChange('snn_execution_mode', newMode);
      setCompileModeWarning('');
      return;
    }
    if (graphId) {
      try {
        const status = await graphBuilderApi.getCompileStatus(graphId);
        if ((newMode === 'compiled_full' || newMode === 'compiled_full_numpy') && !status.compiled_full) {
          setCompileModeWarning('Compiled Full artifacts not found. Compile the graph first.');
          return;
        }
        if (newMode === 'compiled_sharded' && !status.compiled_sharded) {
          setCompileModeWarning('Compiled Sharded artifacts not found.');
          return;
        }
      } catch (_) { /* allow if can't check */ }
    }
    onChange('snn_execution_mode', newMode);
    setCompileModeWarning('');
  };

  const handleVocabToggle = async (checked) => {
    if (checked && graphId) {
      try {
        const result = await apiService.getGraphVocabulary(graphId);
        if (result.success && result.exists) {
          onChange('expand_with_vocabulary', true);
          setVocabWarning('');
        } else {
          setVocabWarning('No vocabulary context cached.');
          onChange('expand_with_vocabulary', false);
        }
      } catch (_) {
        setVocabWarning('Could not check vocabulary status.');
        onChange('expand_with_vocabulary', false);
      }
    } else {
      onChange('expand_with_vocabulary', checked);
      setVocabWarning('');
    }
  };

  return (
    <div className="ab-engine-panel">
      {/* Row 2: Execution Modes */}
      <div className="settings-row">
        <div className="settings-group">
          <label>Execution Mode</label>
          <select value={settings.execution_mode || 'BUILD_AND_RUN'} onChange={(e) => onChange('execution_mode', e.target.value)}>
            {EXECUTION_MODES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="settings-group">
          <label>Engine Mode</label>
          <select value={settings.snn_execution_mode || 'compiled_full_numpy'} onChange={(e) => handleSnnModeChange(e.target.value)}>
            {ENGINE_MODES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {compileModeWarning && <span className="setting-warning">{compileModeWarning}</span>}
        </div>
        <div className="settings-group checkbox-group">
          <label className="checkbox-label">
            <input type="checkbox" checked={!!settings.use_cache} onChange={(e) => onChange('use_cache', e.target.checked)} />
            <span>Use Cached Graph</span>
          </label>
          <span className="setting-hint">Auto-warm compile, pool, and DB caches before execution</span>
        </div>
        <div className="settings-group">
          <label>LLM Model (Fallback)</label>
          <select value={settings.llm_model || ''} onChange={(e) => onChange('llm_model', e.target.value)}>
            {LLM_MODEL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="settings-group">
          <label>Model Preset</label>
          <select
            value={settings.model_preset || 'balanced'}
            onChange={(e) => {
              const preset = e.target.value;
              onChange('model_preset', preset);
              if (preset !== 'custom') {
                onChange('stage_models', { ...MODEL_PRESETS[preset].models });
                setStageModelsOpen(false);
              } else {
                const current = settings.stage_models || MODEL_PRESETS.balanced.models;
                onChange('stage_models', { ...current });
                setStageModelsOpen(true);
              }
            }}
          >
            {Object.entries(MODEL_PRESETS).map(([key, preset]) => (
              <option key={key} value={key}>{preset.label} — {preset.description}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Per-stage model selectors */}
      {settings.stage_models && (
        <div className="snn-tuning-section">
          <div className="snn-tuning-header" onClick={() => setStageModelsOpen(!stageModelsOpen)}>
            <span className="settings-icon">{stageModelsOpen ? '▼' : '▶'}</span>
            <span className="snn-tuning-title">Per-Stage Model Selection</span>
            <span className="setting-hint" style={{ marginLeft: 8 }}>
              {settings.model_preset === 'custom' ? 'Custom configuration' : `Preset: ${MODEL_PRESETS[settings.model_preset || 'balanced']?.label || 'Balanced'}`}
            </span>
          </div>
          {stageModelsOpen && (
            <div className="snn-tuning-body">
              <div className="settings-row stage-models-grid">
                {STAGE_MODEL_KEYS.map(({ key, label, hint }) => (
                  <div className="settings-group" key={key}>
                    <label>{label}</label>
                    <select
                      value={(settings.stage_models || {})[key] || 'gpt-4o'}
                      onChange={(e) => {
                        const updated = { ...(settings.stage_models || {}), [key]: e.target.value };
                        onChange('stage_models', updated);
                        onChange('model_preset', 'custom');
                      }}
                    >
                      {LLM_MODEL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <span className="setting-hint">{hint}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Row 3: Numeric */}
      <div className="settings-row">
        <div className="settings-group">
          <label>Min Similarity</label>
          <input type="number" min="0" max="1" step="0.05"
            value={settings.min_similarity ?? 0.5}
            onChange={(e) => onChange('min_similarity', parseFloat(e.target.value) || 0)} />
          <span className="setting-hint">0-1 threshold for semantic match</span>
        </div>
        <div className="settings-group">
          <label>Similarity Top K</label>
          <input type="number" min="1" max="500"
            value={settings.similarity_top_k ?? 150}
            onChange={(e) => onChange('similarity_top_k', parseInt(e.target.value) || 150)} />
          <span className="setting-hint">Candidate nodes from search</span>
        </div>
        <div className="settings-group checkbox-group">
          <label className="checkbox-label">
            <input type="checkbox" checked={settings.decompose_query ?? true} onChange={(e) => onChange('decompose_query', e.target.checked)} />
            <span>Decompose Query</span>
          </label>
          <span className="setting-hint">Split compound queries</span>
        </div>
      </div>

      {/* Row 4: AQH / streaming */}
      <div className="settings-row">
        <div className="settings-group checkbox-group">
          <label className="checkbox-label">
            <input type="checkbox" checked={settings.stream_response ?? false} onChange={(e) => onChange('stream_response', e.target.checked)} />
            <span>Stream Response</span>
          </label>
        </div>
        <div className="settings-group checkbox-group">
          <label className="checkbox-label">
            <input type="checkbox" checked={settings.enable_abstract_query_handler ?? true} onChange={(e) => onChange('enable_abstract_query_handler', e.target.checked)} />
            <span>Enable AQH</span>
          </label>
        </div>
        <div className="settings-group">
          <label>Gap Fill Iterations</label>
          <input type="number" min="0" max="5"
            value={settings.max_gap_fill_iterations ?? 2}
            onChange={(e) => onChange('max_gap_fill_iterations', parseInt(e.target.value) || 0)}
            disabled={!(settings.enable_abstract_query_handler ?? true)} />
        </div>
        <div className="settings-group">
          <label>Traversal Depth</label>
          <input type="number" min="1" max="5"
            value={settings.graph_traversal_depth ?? 2}
            onChange={(e) => onChange('graph_traversal_depth', parseInt(e.target.value) || 1)}
            disabled={!(settings.enable_abstract_query_handler ?? true)} />
        </div>
        <div className="settings-group checkbox-group">
          <label className="checkbox-label">
            <input type="checkbox" checked={settings.include_execution_trace ?? true} onChange={(e) => onChange('include_execution_trace', e.target.checked)} />
            <span>Include Execution Trace</span>
          </label>
        </div>
        <div className="settings-group checkbox-group">
          <label className="checkbox-label">
            <input type="checkbox" checked={settings.expand_with_vocabulary ?? false} onChange={(e) => handleVocabToggle(e.target.checked)} />
            <span>Vocabulary Query Expansion</span>
          </label>
          {vocabWarning && <span className="setting-warning">{vocabWarning}</span>}
        </div>
      </div>

      {/* Context Bundle */}
      <div className="settings-row">
        <div className="settings-group">
          <label>Context Potency Mode</label>
          <select
            value={settings.context_potency_mode ?? 'auto'}
            onChange={(e) => onChange('context_potency_mode', e.target.value)}
          >
            {CONTEXT_POTENCY_MODE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className="setting-hint">
            Auto = backend picks per-query threshold (knee + p65 + top-K floor) and escalates if Judge flags low confidence
          </span>
        </div>
        {(settings.context_potency_mode ?? 'auto') === 'manual' && (
          <div className="settings-group">
            <label>Context Potency Threshold</label>
            <input type="number" min="0.01" max="0.80" step="0.01"
              value={settings.context_potency_threshold ?? 0.26}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                if (!isNaN(val)) onChange('context_potency_threshold', Math.min(0.80, Math.max(0.01, val)));
              }} />
            <span className="setting-hint">Min potency for LLM context. Range: 0.01-0.80</span>
          </div>
        )}
        {(settings.context_potency_mode ?? 'auto') === 'auto' && (
          <div className="settings-group">
            <label>Auto Potency Boost</label>
            <input type="number" min="0.5" max="3.0" step="0.1"
              value={settings.auto_potency_boost_multiplier ?? 1.3}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                if (!isNaN(val)) onChange(
                  'auto_potency_boost_multiplier',
                  Math.min(3.0, Math.max(0.5, val)),
                );
              }} />
            <span className="setting-hint">Multiplier on auto-picked threshold (raw * boost). Range: 0.5-3.0 (default 1.9). Judge fallback strips back to raw.</span>
          </div>
        )}
        <div className="settings-group checkbox-group">
          <label className="checkbox-label">
            <input type="checkbox" checked={settings.include_verbatim_nodes ?? true} onChange={(e) => onChange('include_verbatim_nodes', e.target.checked)} />
            <span>Include Verbatim Nodes</span>
          </label>
        </div>
        <div className="settings-group checkbox-group">
          <label className="checkbox-label">
            <input type="checkbox" checked={settings.enable_judge ?? true} onChange={(e) => onChange('enable_judge', e.target.checked)} />
            <span>Use Judge LLM</span>
          </label>
        </div>
        <div className="settings-group checkbox-group">
          <label className="checkbox-label">
            <input type="checkbox" checked={settings.enable_context_distillation ?? true} onChange={(e) => onChange('enable_context_distillation', e.target.checked)} />
            <span>Context Distillation</span>
          </label>
        </div>
        <div className="settings-group checkbox-group">
          <label className="checkbox-label">
            <input type="checkbox" checked={settings.enable_cross_encoder_rerank ?? false} onChange={(e) => onChange('enable_cross_encoder_rerank', e.target.checked)} />
            <span>Cross-Encoder Rerank</span>
          </label>
        </div>
      </div>

      {/* MLCF Section */}
      <div className="snn-tuning-section">
        <div className="snn-tuning-header" onClick={() => setMlcfOpen(!mlcfOpen)}>
          <span className="settings-icon">{mlcfOpen ? '▼' : '▶'}</span>
          <span className="snn-tuning-title">Seed Filtering (MLCF)</span>
          <span className="setting-hint" style={{ marginLeft: 8 }}>Multi-Layer Constraint Filtering</span>
        </div>
        {mlcfOpen && (
          <div className="snn-tuning-body">
            <div className="settings-row">
              <div className="settings-group checkbox-group">
                <label className="checkbox-label">
                  <input type="checkbox" checked={settings.mlcf_enabled ?? true} onChange={(e) => onChange('mlcf_enabled', e.target.checked)} />
                  <span>Enable MLCF Filtering</span>
                </label>
              </div>
            </div>
            {(settings.mlcf_enabled ?? true) && (
              <>
                <div className="settings-row">
                  <div className="settings-group checkbox-group">
                    <label className="checkbox-label">
                      <input type="checkbox" checked={settings.mlcf_identity_filter ?? true} onChange={(e) => onChange('mlcf_identity_filter', e.target.checked)} />
                      <span>Identity Mismatch Filter</span>
                    </label>
                    <span className="setting-hint">Prevent nodes with conflicting qualifiers</span>
                  </div>
                  <div className="settings-group checkbox-group">
                    <label className="checkbox-label">
                      <input type="checkbox" checked={settings.mlcf_scope_filter ?? true} onChange={(e) => onChange('mlcf_scope_filter', e.target.checked)} />
                      <span>Scope Intersection Filter</span>
                    </label>
                  </div>
                  <div className="settings-group checkbox-group">
                    <label className="checkbox-label">
                      <input type="checkbox" checked={settings.mlcf_constraint_filter ?? true} onChange={(e) => onChange('mlcf_constraint_filter', e.target.checked)} />
                      <span>Constraint Satisfaction Filter</span>
                    </label>
                  </div>
                </div>
                <div className="settings-row">
                  <div className="settings-group checkbox-group">
                    <label className="checkbox-label">
                      <input type="checkbox" checked={settings.mlcf_context_filter ?? true} onChange={(e) => onChange('mlcf_context_filter', e.target.checked)} />
                      <span>Context Compatibility Filter</span>
                    </label>
                  </div>
                  <div className="settings-group checkbox-group">
                    <label className="checkbox-label">
                      <input type="checkbox" checked={settings.mlcf_structural_filter ?? true} onChange={(e) => onChange('mlcf_structural_filter', e.target.checked)} />
                      <span>Structural Relation Validation</span>
                    </label>
                  </div>
                </div>
                <div className="settings-row">
                  <div className="settings-group">
                    <label>Identity Match Threshold</label>
                    <input type="number" min="0" max="1" step="0.05"
                      value={settings.mlcf_identity_threshold ?? 0.5}
                      onChange={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) onChange('mlcf_identity_threshold', Math.min(1, Math.max(0, v))); }} />
                    <span className="setting-hint">Default: 0.5</span>
                  </div>
                  <div className="settings-group">
                    <label>Scope Overlap Threshold</label>
                    <input type="number" min="0" max="1" step="0.05"
                      value={settings.mlcf_scope_overlap_threshold ?? 0.3}
                      onChange={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) onChange('mlcf_scope_overlap_threshold', Math.min(1, Math.max(0, v))); }} />
                    <span className="setting-hint">Default: 0.3</span>
                  </div>
                  <div className="settings-group">
                    <label>Context Strictness</label>
                    <select value={settings.mlcf_context_strictness ?? 'moderate'} onChange={(e) => onChange('mlcf_context_strictness', e.target.value)}>
                      <option value="strict">Strict</option>
                      <option value="moderate">Moderate</option>
                      <option value="permissive">Permissive</option>
                    </select>
                  </div>
                  <div className="settings-group">
                    <label>Min Survivors</label>
                    <input type="range" min="0" max="10" step="1"
                      value={settings.mlcf_min_survivors ?? 1}
                      onChange={(e) => onChange('mlcf_min_survivors', parseInt(e.target.value))} />
                    <span className="setting-value">{settings.mlcf_min_survivors ?? 1}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* CRS Section */}
      <div className="snn-tuning-section">
        <div className="snn-tuning-header" onClick={() => setCrsOpen(!crsOpen)}>
          <span className="settings-icon">{crsOpen ? '▼' : '▶'}</span>
          <span className="snn-tuning-title">Connection Selection (CRS)</span>
          <span className="setting-hint" style={{ marginLeft: 8 }}>Composite Relevance Scorer</span>
        </div>
        {crsOpen && (
          <div className="snn-tuning-body">
            <div className="settings-row">
              <div className="settings-group checkbox-group">
                <label className="checkbox-label">
                  <input type="checkbox" checked={settings.crs_enabled ?? true} onChange={(e) => onChange('crs_enabled', e.target.checked)} />
                  <span>Enable Composite Relevance Scorer</span>
                </label>
              </div>
            </div>
            {(settings.crs_enabled ?? true) && (
              <>
                <div className="settings-row">
                  <div className="settings-group">
                    <label>Semantic Weight</label>
                    <input type="range" min="0" max="0.5" step="0.01" value={settings.crs_semantic_weight ?? 0.25} onChange={(e) => onChange('crs_semantic_weight', parseFloat(e.target.value))} />
                    <span className="setting-hint">{(settings.crs_semantic_weight ?? 0.25).toFixed(2)}</span>
                  </div>
                  <div className="settings-group">
                    <label>Node-Type Compat</label>
                    <input type="range" min="0" max="0.5" step="0.01" value={settings.crs_node_type_compat_weight ?? 0.15} onChange={(e) => onChange('crs_node_type_compat_weight', parseFloat(e.target.value))} />
                    <span className="setting-hint">{(settings.crs_node_type_compat_weight ?? 0.15).toFixed(2)}</span>
                  </div>
                  <div className="settings-group">
                    <label>Intent Affinity</label>
                    <input type="range" min="0" max="0.5" step="0.01" value={settings.crs_intent_affinity_weight ?? 0.15} onChange={(e) => onChange('crs_intent_affinity_weight', parseFloat(e.target.value))} />
                    <span className="setting-hint">{(settings.crs_intent_affinity_weight ?? 0.15).toFixed(2)}</span>
                  </div>
                  <div className="settings-group">
                    <label>Scope Intersection</label>
                    <input type="range" min="0" max="0.5" step="0.01" value={settings.crs_scope_intersection_weight ?? 0.15} onChange={(e) => onChange('crs_scope_intersection_weight', parseFloat(e.target.value))} />
                    <span className="setting-hint">{(settings.crs_scope_intersection_weight ?? 0.15).toFixed(2)}</span>
                  </div>
                </div>
                <div className="settings-row">
                  <div className="settings-group">
                    <label>Causal / Modality</label>
                    <input type="range" min="0" max="0.5" step="0.01" value={settings.crs_causal_modality_weight ?? 0.10} onChange={(e) => onChange('crs_causal_modality_weight', parseFloat(e.target.value))} />
                    <span className="setting-hint">{(settings.crs_causal_modality_weight ?? 0.10).toFixed(2)}</span>
                  </div>
                  <div className="settings-group">
                    <label>Authority</label>
                    <input type="range" min="0" max="0.5" step="0.01" value={settings.crs_authority_weight ?? 0.08} onChange={(e) => onChange('crs_authority_weight', parseFloat(e.target.value))} />
                    <span className="setting-hint">{(settings.crs_authority_weight ?? 0.08).toFixed(2)}</span>
                  </div>
                  <div className="settings-group">
                    <label>Gate Readiness</label>
                    <input type="range" min="0" max="0.5" step="0.01" value={settings.crs_gate_readiness_weight ?? 0.07} onChange={(e) => onChange('crs_gate_readiness_weight', parseFloat(e.target.value))} />
                    <span className="setting-hint">{(settings.crs_gate_readiness_weight ?? 0.07).toFixed(2)}</span>
                  </div>
                  <div className="settings-group">
                    <label>Exception Alignment</label>
                    <input type="range" min="0" max="0.5" step="0.01" value={settings.crs_exception_alignment_weight ?? 0.05} onChange={(e) => onChange('crs_exception_alignment_weight', parseFloat(e.target.value))} />
                    <span className="setting-hint">{(settings.crs_exception_alignment_weight ?? 0.05).toFixed(2)}</span>
                  </div>
                </div>
                <div className="settings-row">
                  <div className="settings-group">
                    <label>Evidence Threshold</label>
                    <input type="range" min="0.05" max="0.8" step="0.05" value={settings.crs_evidence_threshold ?? 0.3} onChange={(e) => onChange('crs_evidence_threshold', parseFloat(e.target.value))} />
                    <span className="setting-hint">{(settings.crs_evidence_threshold ?? 0.3).toFixed(2)}</span>
                  </div>
                  <div className="settings-group">
                    <label>Default Unscored Relevance</label>
                    <input type="range" min="0.05" max="0.8" step="0.05" value={settings.crs_default_unscored_relevance ?? 0.3} onChange={(e) => onChange('crs_default_unscored_relevance', parseFloat(e.target.value))} />
                    <span className="setting-hint">{(settings.crs_default_unscored_relevance ?? 0.3).toFixed(2)}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* SER Section */}
      <div className="snn-tuning-section">
        <div className="snn-tuning-header" onClick={() => setSerOpen(!serOpen)}>
          <span className="settings-icon">{serOpen ? '▼' : '▶'}</span>
          <span className="engine-tuning-title">Semantic Receptors</span>
          <span className="setting-hint" style={{ marginLeft: 8 }}>Embedding-native relevance scoring</span>
        </div>
        {serOpen && (
          <div className="snn-tuning-body">
            <div className="settings-row">
              <div className="settings-group checkbox-group">
                <label className="checkbox-label">
                  <input type="checkbox" checked={settings.enable_ser ?? true} onChange={(e) => onChange('enable_ser', e.target.checked)} />
                  <span>Enable SER Selection</span>
                </label>
              </div>
            </div>
            {(settings.enable_ser ?? true) && (
              <div className="settings-row">
                <div className="settings-group">
                  <label>SER Temperature</label>
                  <input type="range" min="0.05" max="2.0" step="0.05" value={settings.ser_temperature ?? 0.35} onChange={(e) => onChange('ser_temperature', parseFloat(e.target.value))} />
                  <span className="setting-hint">{(settings.ser_temperature ?? 0.35).toFixed(2)}</span>
                </div>
                <div className="settings-group">
                  <label>BM25 Additive Boost</label>
                  <input type="range" min="0" max="1.0" step="0.05" value={settings.ser_bm25_boost ?? 0.1} onChange={(e) => onChange('ser_bm25_boost', parseFloat(e.target.value))} />
                  <span className="setting-hint">{(settings.ser_bm25_boost ?? 0.1).toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Platform Tuning (compiled modes only) */}
      {isCompiledMode && (
        <div className="snn-tuning-section">
          <div className="snn-tuning-header" onClick={() => setSnnTuningOpen(!snnTuningOpen)}>
            <span className="settings-icon">{snnTuningOpen ? '▼' : '▶'}</span>
            <span className="snn-tuning-title">Platform Tuning Parameters</span>
            <span className="setting-hint" style={{ marginLeft: 8 }}>Simulation &amp; scoring hyperparameters</span>
          </div>
          {snnTuningOpen && (
            <div className="snn-tuning-body">
              <div className="settings-row">
                <div className="settings-group">
                  <label>Simulate Duration (ms)</label>
                  <input type="number" min="50" max="2000" step="10" value={settings.simulate_duration_ms ?? 200} onChange={(e) => onChange('simulate_duration_ms', parseFloat(e.target.value) || 200)} />
                  <span className="setting-hint">Default: 200</span>
                </div>
                <div className="settings-group">
                  <label>Simulate Step (ms)</label>
                  <input type="number" min="5" max="200" step="5" value={settings.simulate_step_ms ?? 20} onChange={(e) => onChange('simulate_step_ms', parseFloat(e.target.value) || 20)} />
                  <span className="setting-hint">Default: 20</span>
                </div>
                <div className="settings-group">
                  <label>Max Recursive Ticks</label>
                  <input type="number" min="1" max="50" step="1" value={settings.max_recursive_ticks ?? 10} onChange={(e) => onChange('max_recursive_ticks', parseInt(e.target.value) || 10)} />
                  <span className="setting-hint">Default: 10</span>
                </div>
                <div className="settings-group">
                  <label>Bias Level</label>
                  <input type="number" min="0" max="200" step="5" value={settings.bias_hz ?? 50} onChange={(e) => onChange('bias_hz', parseFloat(e.target.value) || 0)} />
                  <span className="setting-hint">Default: 50</span>
                </div>
                <div className="settings-group">
                  <label>Gain</label>
                  <input type="number" min="1" max="100" step="1" value={settings.gain ?? 20} onChange={(e) => onChange('gain', parseFloat(e.target.value) || 20)} />
                  <span className="setting-hint">Default: 20</span>
                </div>
                <div className="settings-group">
                  <label>Min Background Level</label>
                  <input type="number" min="-10" max="10" step="0.5" value={settings.min_background_rate_hz ?? 0} onChange={(e) => onChange('min_background_rate_hz', parseFloat(e.target.value) || 0)} />
                  <span className="setting-hint">Default: 0</span>
                </div>
              </div>
              <div className="settings-row">
                <div className="settings-group">
                  <label>Scoring Min Measure</label>
                  <input type="number" min="0" max="100" step="1" value={settings.rate_coding_min_hz ?? 8} onChange={(e) => onChange('rate_coding_min_hz', parseFloat(e.target.value) || 0)} />
                  <span className="setting-hint">Default: 8</span>
                </div>
                <div className="settings-group">
                  <label>Scoring Max Measure</label>
                  <input type="number" min="50" max="500" step="10" value={settings.rate_coding_max_hz ?? 220} onChange={(e) => onChange('rate_coding_max_hz', parseFloat(e.target.value) || 220)} />
                  <span className="setting-hint">Default: 220</span>
                </div>
                <div className="settings-group">
                  <label>Scoring Base Measure</label>
                  <input type="number" min="5" max="200" step="5" value={settings.rate_coding_base_hz ?? 50} onChange={(e) => onChange('rate_coding_base_hz', parseFloat(e.target.value) || 50)} />
                  <span className="setting-hint">Default: 50</span>
                </div>
                <div className="settings-group">
                  <label>Softmax Temperature</label>
                  <input type="number" min="0.05" max="2.0" step="0.05" value={settings.softmax_temperature ?? 0.35} onChange={(e) => onChange('softmax_temperature', parseFloat(e.target.value) || 0.35)} />
                  <span className="setting-hint">Default: 0.35</span>
                </div>
              </div>
              <div className="settings-row">
                <div className="settings-group">
                  <label>Propagation Evidence Threshold</label>
                  <input type="range" min="0.01" max="0.5" step="0.01" value={settings.propagation_evidence_threshold ?? 0.15} onChange={(e) => onChange('propagation_evidence_threshold', parseFloat(e.target.value))} />
                  <span className="setting-hint">{(settings.propagation_evidence_threshold ?? 0.15).toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ABSettingsModal({ settings, onUpdate, onClose, nodes }) {
  const [mode, setMode] = useState('global');
  const [selectedNode, setSelectedNode] = useState(null);

  const globalSNN = settings.globalSNN || {};
  const globalLLM = settings.globalLLM || {};
  const overrides = settings.nodeOverrides || {};

  const updateGlobalSNN = (key, val) => {
    onUpdate({ ...settings, globalSNN: { ...globalSNN, [key]: val } });
  };

  const updateGlobalLLM = (key, val) => {
    onUpdate({ ...settings, globalLLM: { ...globalLLM, [key]: val } });
  };

  const applyToAllSNN = () => {
    const newOverrides = { ...overrides };
    (nodes || []).filter((n) => n.data.executionType === 'snn_pipeline').forEach((n) => {
      const existing = newOverrides[n.id];
      if (existing?.pinned) return;
      newOverrides[n.id] = { ...(existing || {}), pipeline_settings: { ...globalSNN } };
    });
    onUpdate({ ...settings, nodeOverrides: newOverrides });
  };

  const applyToAllLLM = () => {
    const newOverrides = { ...overrides };
    (nodes || []).filter((n) => n.data.executionType === 'llm').forEach((n) => {
      const existing = newOverrides[n.id];
      if (existing?.pinned) return;
      newOverrides[n.id] = { ...(existing || {}), ...globalLLM };
    });
    onUpdate({ ...settings, nodeOverrides: newOverrides });
  };

  const updateNodeOverride = (nodeId, key, val) => {
    const existing = overrides[nodeId] || {};
    const ps = existing.pipeline_settings || {};
    const isLLMKey = ['model', 'temperature', 'max_tokens'].includes(key);
    const newEntry = isLLMKey
      ? { ...existing, [key]: val, pinned: true }
      : { ...existing, pipeline_settings: { ...ps, [key]: val }, pinned: true };
    onUpdate({ ...settings, nodeOverrides: { ...overrides, [nodeId]: newEntry } });
  };

  const snnNodes = (nodes || []).filter((n) => n.data.executionType === 'snn_pipeline');
  const llmNodes = (nodes || []).filter((n) => n.data.executionType === 'llm');
  const selNodeData = selectedNode ? (nodes || []).find((n) => n.id === selectedNode) : null;

  return (
    <div className="ab-settings-overlay" onClick={onClose}>
      <div className="ab-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Execution Settings</h2>
          <div className="mode-toggle">
            <button className={mode === 'global' ? 'active' : ''} onClick={() => { setMode('global'); setSelectedNode(null); }}>Global by Type</button>
            <button className={mode === 'node' ? 'active' : ''} onClick={() => setMode('node')}>Per-Node</button>
          </div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {mode === 'global' && (
            <>
              {/* SNN Global — full panel */}
              <div className="settings-section">
                <div className="section-header">
                  <h3>Engine Pipeline Settings</h3>
                  <button className="apply-btn" onClick={applyToAllSNN}>Apply to All Engine ({snnNodes.length})</button>
                </div>
                <SNNSettingsPanel settings={globalSNN} onChange={updateGlobalSNN} graphId={null} />
              </div>

              {/* LLM Global */}
              <div className="settings-section">
                <div className="section-header">
                  <h3>LLM Node Settings</h3>
                  <button className="apply-btn" onClick={applyToAllLLM}>Apply to All LLM ({llmNodes.length})</button>
                </div>
                <div className="settings-row">
                  <div className="settings-group">
                    <label>Model</label>
                    <select value={globalLLM.model || 'gpt-4o'} onChange={(e) => updateGlobalLLM('model', e.target.value)}>
                      <option value="gpt-4o">GPT-4o</option>
                      <option value="gpt-4o-mini">GPT-4o Mini</option>
                      <option value="gpt-5.2">GPT-5.2</option>
                      <option value="gpt-5.2-mini">GPT-5.2 Mini</option>
                      <option value="o3">o3</option>
                      <option value="o3-mini">o3-mini</option>
                      <option value="o4-mini">o4-mini</option>
                    </select>
                  </div>
                  <div className="settings-group">
                    <label>Temperature</label>
                    <input type="number" min="0" max="2" step="0.05" value={globalLLM.temperature ?? 0.3} onChange={(e) => updateGlobalLLM('temperature', parseFloat(e.target.value))} />
                  </div>
                  <div className="settings-group">
                    <label>Max Tokens</label>
                    <input type="number" min="256" max="16384" step="256" value={globalLLM.max_tokens ?? 4096} onChange={(e) => updateGlobalLLM('max_tokens', parseInt(e.target.value))} />
                  </div>
                </div>
              </div>
            </>
          )}

          {mode === 'node' && !selectedNode && (
            <div className="node-picker">
              <h3>Select a node to customize:</h3>
              <div className="node-list">
                {[...snnNodes, ...llmNodes].map((n) => {
                  const isPinned = overrides[n.id]?.pinned;
                  return (
                    <button key={n.id} className={`node-pick-btn ${isPinned ? 'pinned' : ''}`} onClick={() => setSelectedNode(n.id)}>
                      <span className={`type-letter ${n.data.executionType}`}>{n.data.typeLetter}</span>
                      {n.data.label}
                      {isPinned && <span className="pin-icon" title="Custom (pinned)">&#128295;</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {mode === 'node' && selectedNode && selNodeData && (
            <div className="per-node-settings">
              <button className="back-link" onClick={() => setSelectedNode(null)}>&larr; Back to node list</button>
              <h3>
                <span className={`type-letter ${selNodeData.data.executionType}`}>{selNodeData.data.typeLetter}</span>
                {selNodeData.data.label}
                {overrides[selectedNode]?.pinned && <span className="pin-icon">&#128295; Pinned</span>}
              </h3>
              {selNodeData.data.executionType === 'snn_pipeline' && (
                <SNNSettingsPanel
                  settings={(() => {
                    const nodePS = overrides[selectedNode]?.pipeline_settings || {};
                    return { ...globalSNN, ...nodePS };
                  })()}
                  onChange={(k, v) => updateNodeOverride(selectedNode, k, v)}
                  graphId={null}
                />
              )}
              {selNodeData.data.executionType === 'llm' && (
                <div className="settings-row">
                  <div className="settings-group">
                    <label>Model</label>
                    <select value={overrides[selectedNode]?.model || globalLLM.model || 'gpt-4o'} onChange={(e) => updateNodeOverride(selectedNode, 'model', e.target.value)}>
                      <option value="gpt-4o">GPT-4o</option>
                      <option value="gpt-4o-mini">GPT-4o Mini</option>
                      <option value="gpt-5.2">GPT-5.2</option>
                      <option value="gpt-5.2-mini">GPT-5.2 Mini</option>
                      <option value="o3">o3</option>
                      <option value="o3-mini">o3-mini</option>
                      <option value="o4-mini">o4-mini</option>
                    </select>
                  </div>
                  <div className="settings-group">
                    <label>Temperature</label>
                    <input type="number" min="0" max="2" step="0.05" value={overrides[selectedNode]?.temperature ?? globalLLM.temperature ?? 0.3} onChange={(e) => updateNodeOverride(selectedNode, 'temperature', parseFloat(e.target.value))} />
                  </div>
                  <div className="settings-group">
                    <label>Max Tokens</label>
                    <input type="number" min="256" max="16384" step="256" value={overrides[selectedNode]?.max_tokens ?? globalLLM.max_tokens ?? 4096} onChange={(e) => updateNodeOverride(selectedNode, 'max_tokens', parseInt(e.target.value))} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
